
import { defineComponent } from "vue";
import {
  useProfilePermissionsStore,
  ProfilePermissions,
} from "@/stores/profilePermissions";
import { useMenuStore } from "@/stores/menu";
import { MenuItem } from "@/stores/menu";
import { VIEW_MODE } from "@/utils/menuScope";

export default defineComponent({
  name: "ProfilePermissionsConfiguration",
  setup() {
    const profilePermissionsStore = useProfilePermissionsStore();
    const menuStore = useMenuStore();
    return {
      profilePermissionsStore,
      menuStore,
    };
  },
  computed: {
    UserMenu(): MenuItem[] {
      return this.menuStore.menus;
    },
    sortedMenus(): MenuItem[] {
      // Sort menus by Priority
      return [...this.UserMenu].sort((a, b) => a.Priority - b.Priority);
    },
    filteredMenus(): MenuItem[] {
      return this.sortedMenus.filter((menu) => {
        // Filter by visibility
        if (this.hiddenMenuKeys.includes(menu.Key)) {
          return false;
        }

        // Filter by scope
        if (this.scopeFilter === "all") {
          return true;
        } else if (this.scopeFilter === "admin") {
          // Show only admin-scoped items
          if (!menu.Scope) return false;
          if (Array.isArray(menu.Scope)) {
            return menu.Scope.includes(VIEW_MODE.ADMIN);
          }
          return menu.Scope === VIEW_MODE.ADMIN;
        } else if (this.scopeFilter === "user") {
          // Show user-scoped items or items without scope (default to user)
          if (!menu.Scope) return true;
          if (Array.isArray(menu.Scope)) {
            return menu.Scope.includes(VIEW_MODE.USER);
          }
          return menu.Scope === VIEW_MODE.USER;
        }

        return true;
      });
    },
    visibleProfiles(): ProfilePermissions[] {
      return this.profiles.filter(
        (p) => !this.hiddenProfileIds.includes(p.profileId)
      );
    },
    hasChanges(): boolean {
      // Check if there are any changes to save, ignoring order of profiles
      // and the order of entries in each profile's `data` array.
      const normalize = (profiles: ProfilePermissions[]) =>
        profiles
          .map((profile) => ({
            ...profile,
            data: Array.isArray((profile as any).data)
              ? [...(profile as any).data].sort()
              : (profile as any).data,
          }))
          .sort((a, b) => {
            if (a.profileId < b.profileId) return -1;
            if (a.profileId > b.profileId) return 1;
            return 0;
          });

      const normalizedCurrent = normalize(this.profiles);
      const normalizedOriginal = normalize(this.originalProfiles);

      return (
        JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedOriginal)
      );
    },
  },
  data() {
    return {
      addMode: false,
      loading: false,
      profiles: Array<ProfilePermissions>(),
      originalProfiles: Array<ProfilePermissions>(),
      newProfile: {
        profileId: "",
        active: true,
        data: Array<string>(),
      },
      hiddenProfileIds: [] as string[],
      hiddenMenuKeys: [] as string[],
      scopeFilter: "all", // 'all', 'admin', 'user'
      showColumnSelector: false,
      showMenuSelector: false,
      showScopeSelector: false,
      hoveredRow: null as string | null,
      hoveredColumn: null as string | null,
      pendingChanges: new Set<string>(), // Track modified checkboxes
    };
  },
  created() {
    this.loadProfiles();
  },
  methods: {
    async loadProfiles() {
      this.loading = true;
      try {
        const response = await this.profilePermissionsStore.fetchAllProfiles();
        this.profiles = response.map((profile) => ({
          profileId: profile.profileId,
          active: profile.active !== undefined ? profile.active : true,
          data: profile.data || [],
        }));
        // Keep a deep copy of original profiles for change detection
        this.originalProfiles = JSON.parse(JSON.stringify(this.profiles));
        this.pendingChanges.clear();
      } catch (error: any) {
        console.error(error);
        alert("Error al cargar perfiles: " + error.message);
      } finally {
        this.loading = false;
      }
    },
    hasPermission(profile: ProfilePermissions, screenKey: string): boolean {
      return profile.data?.includes(screenKey) || false;
    },
    togglePermission(profile: ProfilePermissions, screenKey: string) {
      const data = profile.data || [];
      const index = data.indexOf(screenKey);

      if (index > -1) {
        // Remove permission
        data.splice(index, 1);
      } else {
        // Add permission
        data.push(screenKey);
      }

      profile.data = data;

      // Check if this change differs from the original state
      const changeKey = `${profile.profileId}-${screenKey}`;
      const originalProfile = this.originalProfiles.find(
        (p) => p.profileId === profile.profileId
      );

      if (originalProfile) {
        const hadPermissionOriginally =
          originalProfile.data?.includes(screenKey) || false;
        const hasPermissionNow = profile.data?.includes(screenKey) || false;

        if (hadPermissionOriginally === hasPermissionNow) {
          // State reverted to original, remove from pending changes
          this.pendingChanges.delete(changeKey);
        } else {
          // State is different from original, mark as pending
          this.pendingChanges.add(changeKey);
        }
      }
    },
    toggleProfileActive(profile: ProfilePermissions) {
      profile.active = !profile.active;

      // Check if this change differs from the original state
      const changeKey = `${profile.profileId}-active`;
      const originalProfile = this.originalProfiles.find(
        (p) => p.profileId === profile.profileId
      );

      if (originalProfile) {
        const originalActive = originalProfile.active !== false;
        const currentActive = profile.active !== false;

        if (originalActive === currentActive) {
          // State reverted to original, remove from pending changes
          this.pendingChanges.delete(changeKey);
        } else {
          // State is different from original, mark as pending
          this.pendingChanges.add(changeKey);
        }
      }
    },
    async saveAllChanges() {
      this.loading = true;
      try {
        const response =
          await this.profilePermissionsStore.updateMultipleProfiles(
            this.profiles
          );

        if (response.OPCODE === "SUCCESS") {
          // Update original profiles after successful save
          this.originalProfiles = JSON.parse(JSON.stringify(this.profiles));
          // Clear all pending changes
          this.pendingChanges.clear();
          await this.loadProfiles();
          alert("Cambios guardados exitosamente");
        } else {
          throw new Error(response.message);
        }
      } catch (error: any) {
        alert("Error al guardar cambios: " + error.message);
      } finally {
        this.loading = false;
      }
    },
    createProfile() {
      this.addMode = true;
      this.newProfile.profileId = "";
      this.newProfile.active = true;
      this.newProfile.data = [];
    },
    async saveProfile() {
      if (!this.newProfile.profileId.trim()) {
        alert("Por favor ingrese un ID de perfil válido");
        return;
      }

      // Check if profile already exists
      if (
        this.profiles.some((p) => p.profileId === this.newProfile.profileId)
      ) {
        alert("Ya existe un perfil con este ID");
        return;
      }

      this.loading = true;

      try {
        const response = await this.profilePermissionsStore.addProfile(
          this.newProfile
        );

        if (response.OPCODE === "SUCCESS") {
          this.addMode = false;
          alert(response.message);
          await this.loadProfiles(); // Reload to sync with backend
        } else {
          throw new Error(response.message);
        }
      } catch (error: any) {
        alert("Error: " + error.message);
      } finally {
        this.loading = false;
      }
    },
    cancel() {
      this.addMode = false;
      this.newProfile.profileId = "";
      this.newProfile.active = true;
      this.newProfile.data = [];
    },
    setHover(screenKey: string | null, profileId: string) {
      this.hoveredRow = screenKey;
      this.hoveredColumn = profileId;
    },
    clearHover() {
      this.hoveredRow = null;
      this.hoveredColumn = null;
    },
    isRowHovered(screenKey: string): boolean {
      return this.hoveredRow === screenKey;
    },
    isColumnHovered(profileId: string): boolean {
      return this.hoveredColumn === profileId;
    },
    toggleColumnVisibility(profileId: string) {
      const index = this.hiddenProfileIds.indexOf(profileId);
      if (index === -1) {
        this.hiddenProfileIds.push(profileId);
      } else {
        this.hiddenProfileIds.splice(index, 1);
      }
    },
    toggleMenuVisibility(menuKey: string) {
      const index = this.hiddenMenuKeys.indexOf(menuKey);
      if (index === -1) {
        this.hiddenMenuKeys.push(menuKey);
      } else {
        this.hiddenMenuKeys.splice(index, 1);
      }
    },
    selectAllProfiles() {
      this.hiddenProfileIds = [];
    },
    deselectAllProfiles() {
      this.hiddenProfileIds = this.profiles.map((p) => p.profileId);
    },
    selectAllMenus() {
      this.hiddenMenuKeys = [];
    },
    deselectAllMenus() {
      this.hiddenMenuKeys = this.sortedMenus.map((m) => m.Key);
    },
    getIconForScreen(screen: MenuItem): string[] {
      return [screen.Icon, `text-${screen.Color}`, "me-2", "screen-icon"];
    },
    isPendingChange(profileId: string, screenKey: string): boolean {
      const changeKey = `${profileId}-${screenKey}`;
      return this.pendingChanges.has(changeKey);
    },
    isProfileActiveModified(profileId: string): boolean {
      const changeKey = `${profileId}-active`;
      return this.pendingChanges.has(changeKey);
    },
    setScopeFilter(scope: string) {
      this.scopeFilter = scope;
      this.showScopeSelector = false;
    },
    handleBeforeUnload(e: BeforeUnloadEvent) {
      if (this.hasChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    },
  },
  mounted() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasChanges) {
      const answer = window.confirm(
        "Tienes cambios sin guardar. ¿Estás seguro de que deseas salir?"
      );
      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
});
